import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [form, setForm] = useState({ new_password: '', confirm_password: '' });
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('This reset link is invalid or has expired.');
    }
  }, [token]);

  useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => navigate('/login'), 3000);
      return () => clearTimeout(timer);
    }
  }, [status, navigate]);

  const handleSubmit = async () => {
    if (!form.new_password || !form.confirm_password) {
      setError('Please fill in both fields.');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setError('Passwords do not match.');
      return;
    }
    if (form.new_password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setError('');
    setStatus('loading');
    try {
      await axios.post('/auth/reset-password', { token, new_password: form.new_password });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.error || 'This reset link is invalid or has expired.');
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: 'var(--accent-blue-dim)',
            border: '1px solid var(--accent-blue)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, marginBottom: 14
          }}>⇄</div>
          <h1 className="page-title" style={{ fontSize: 22, letterSpacing: -0.5 }}>SyncBridge</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
            Procore ↔ QuickBooks Automation
          </p>
        </div>

        <div className="card card-glass" style={{ padding: 28 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Set a new password</h3>

          {status === 'success' && (
            <div>
              <div className="alert alert-success" style={{ marginBottom: 16 }}>
                Password reset successfully. Redirecting to sign in...
              </div>
              <div style={{ textAlign: 'center' }}>
                <div className="spinner" style={{ margin: '0 auto' }} />
              </div>
            </div>
          )}

          {status === 'error' && !form.new_password && (
            <div className="alert alert-error">
              {error}
              <div style={{ marginTop: 12 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => navigate('/login')}>
                  ← Back to sign in
                </button>
              </div>
            </div>
          )}

          {(status === 'idle' || status === 'loading' || (status === 'error' && form.new_password)) && (
            <>
              {error && (
                <div className="alert alert-error" style={{ marginBottom: 16 }}>
                  {error}
                </div>
              )}
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  className="form-input"
                  type="password"
                  placeholder="••••••••"
                  value={form.new_password}
                  onChange={e => setForm({ ...form, new_password: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input
                  className="form-input"
                  type="password"
                  placeholder="••••••••"
                  value={form.confirm_password}
                  onChange={e => setForm({ ...form, confirm_password: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                />
              </div>
              <button
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}
                onClick={handleSubmit}
                disabled={status === 'loading'}
              >
                {status === 'loading'
                  ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Resetting...</>
                  : 'Reset Password'}
              </button>
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={() => navigate('/login')}>
                ← Back to sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
